import { DEFAULT_TIME_FORMAT, type TimeFormat, timeParts } from '@/lib/time';

/**
 * Format an ISO datetime string into separate date and time parts, in an
 * EXPLICIT timezone — never the browser's (FRONTEND_PATTERNS.md § Timezones).
 * The time part carries the timezone label (e.g. "10:00 UTC") so the viewer
 * always knows which zone they are looking at.
 *
 * Callers pass the viewer's profile timezone (`profileData.user.timezone` —
 * always resolved by the backend).
 *
 * Extracted from CalendarEventCard for shared use by
 * MeetingCardContent and DateTimeBox consumers.
 */
export function formatDateTimeCompact(
  isoString: string | null | undefined,
  timezone: string,
  format: TimeFormat = DEFAULT_TIME_FORMAT
): {
  date: string;
  time: string;
} {
  // `new Date(null)` resolves to the 1970 epoch instead of throwing — guard
  // before constructing so an unresolved time never renders as a real date.
  if (!isoString) {
    return { date: '', time: '' };
  }
  const d = new Date(isoString);
  if (isNaN(d.getTime())) {
    return { date: isoString, time: '' };
  }
  try {
    return {
      date: d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
      }),
      time: d.toLocaleTimeString('en-US', {
        ...timeParts(format),
        timeZoneName: 'short',
        timeZone: timezone,
      }),
    };
  } catch {
    // An invalid timezone cannot happen for backend-resolved profile values;
    // defend anyway with labeled UTC rather than unlabeled browser-local.
    if (timezone === 'UTC') {
      // UTC itself failed — formatting is impossible; surface the raw value.
      return { date: isoString, time: '' };
    }
    return formatDateTimeCompact(isoString, 'UTC', format);
  }
}
